import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import {
  IThresholdsApiResponse,
  ITransactionThreshold,
  TThresholdType,
} from './transactionThresholdsTypes';

interface ISetTransactionThresholdInput {
  type: TThresholdType;
  amount: number;
  silent?: boolean;
}

const initialState: IApiState<
  IThresholdsApiResponse<ITransactionThreshold>
> = {
  value: null,
  status: 'idle',
  error: '',
};

export const setTransactionThresholdAsync = createAsyncThunk(
  'business-settings/2fa-thresholds/set',
  async (
    { type, amount, silent }: ISetTransactionThresholdInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const { data } = await api.post<
        IThresholdsApiResponse<ITransactionThreshold>
      >(
        '/business-settings/2fa-thresholds',
        { type, amount },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      if (!silent) {
        dispatch(
          showAlert({
            status: 'success',
            description: data?.message ?? '2FA threshold has been set',
          }),
        );
      }

      return data;
    } catch (err) {
      const error = err as AxiosError<
        IThresholdsApiResponse<ITransactionThreshold>
      >;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      if (!silent) {
        dispatch(showAlert({ status: 'error', description: errorMessage }));
      }

      return rejectWithValue({
        ...(error.response?.data ?? {}),
        message: error.response?.data?.message ?? errorMessage,
      });
    }
  },
);

export const setTransactionThresholdSlice = createSlice({
  name: 'business-settings/2fa-thresholds/set',
  initialState,
  reducers: {
    resetSetTransactionThreshold: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setTransactionThresholdAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(setTransactionThresholdAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(setTransactionThresholdAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IThresholdsApiResponse<
          ITransactionThreshold
        >;
        state.error = action.error.message;
      });
  },
});

export const { resetSetTransactionThreshold } =
  setTransactionThresholdSlice.actions;

export default setTransactionThresholdSlice.reducer;
